import React, { useState, useRef, useEffect } from 'react';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from '../exportUtils';
import { accountingApi } from '../../../services/accountingApi';

const StatusBadge = ({ status }) => {
  const s = (status || '').toLowerCase() === 'approved' || (status || '').toLowerCase() === 'completed'
    ? { background: '#dcfce7', color: '#166534' }
    : { background: '#fef9c3', color: '#854d0e' };
  return (
    <span style={{ ...s, padding: '0.25rem 0.7rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
      {status || 'Pending'}
    </span>
  );
};

const inputStyle = {
  width: '100%',
  padding: '0.7rem 0.875rem',
  border: '1px solid #cbd5e1',
  borderRadius: '4px',
  fontSize: '0.9rem',
  outline: 'none',
  boxSizing: 'border-box',
  background: '#fff',
  color: '#1e293b',
};

const Transfer = () => {
  const [transfers, setTransfers]   = useState([]);
  const [accounts, setAccounts]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [search, setSearch]         = useState('');
  const [showModal, setShowModal]   = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [form, setForm]             = useState({ ref: '', from_account_id: '', to_account_id: '', amount: '', date: '', note: '' });
  const [errors, setErrors]         = useState({});
  const exportRef = useRef(null);

  const loadData = () => {
    setLoading(true);
    Promise.all([accountingApi.getTransfers(), accountingApi.getAccounts()])
      .then(([trRes, acRes]) => {
        const toArr = r => Array.isArray(r) ? r : Array.isArray(r.data) ? r.data : Array.isArray(r.data?.data) ? r.data.data : [];
        setTransfers(toArr(trRes));
        setAccounts(toArr(acRes));
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    const handler = (e) => { if (exportRef.current && !exportRef.current.contains(e.target)) setShowExport(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const accountName = (id) => accounts.find(a => String(a.id) === String(id))?.name || id || '—';

  const filtered = transfers.filter(t => {
    const from = accountName(t.from_account_id || t.from_account);
    const to   = accountName(t.to_account_id   || t.to_account);
    const ref  = t.ref_no || String(t.id) || '';
    return from.toLowerCase().includes(search.toLowerCase()) ||
           to.toLowerCase().includes(search.toLowerCase()) ||
           ref.includes(search);
  });

  const openModal = () => {
    setForm({ ref: '', from_account_id: '', to_account_id: '', amount: '', date: '', note: '' });
    setErrors({});
    setShowModal(true);
  };

  const validate = () => {
    const e = {};
    if (!form.from_account_id)                         e.from   = true;
    if (!form.to_account_id)                           e.to     = true;
    if (!form.amount || parseFloat(form.amount) <= 0)  e.amount = true;
    if (!form.date)                                    e.date   = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await accountingApi.createTransfer({
        ref_no:          form.ref,
        from_account_id: form.from_account_id,
        to_account_id:   form.to_account_id,
        amount:          parseFloat(form.amount),
        transfer_date:   form.date,
        additional_notes: form.note,
      });
      setShowModal(false);
      loadData();
    } catch (e) {
      alert(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this transfer?')) return;
    try {
      await accountingApi.deleteTransfer(id);
      loadData();
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    }
  };

  const handleExport = (format) => {
    if (!filtered.length) { alert('No data to export'); setShowExport(false); return; }
    const headers = ['Transfer #', 'Date', 'From Account', 'To Account', 'Amount', 'Note', 'Status'];
    const rows = filtered.map(t => [
      t.id,
      t.transfer_date || t.date || '—',
      accountName(t.from_account_id || t.from_account),
      accountName(t.to_account_id   || t.to_account),
      (parseFloat(t.amount) || 0).toFixed(2),
      t.additional_notes || t.note || '',
      t.status || 'Pending',
    ]);
    if (format === 'CSV')   exportToCSV(headers, rows, 'transfers');
    if (format === 'Excel') exportToExcel(headers, rows, 'transfers');
    if (format === 'PDF')   exportToPDF('Fund Transfers', headers, rows, 'transfers');
    if (format === 'Print') printPage();
    setShowExport(false);
  };

  const totalAmount = transfers.reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading transfers...</div>;
  if (error)   return <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>Error: {error}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      
      {/* ── Stats ── */}
      <div className="acc-stats-row">
        {[
          { label: 'Total Transfers', value: transfers.length, color: 'blue' },
          { label: 'Completed', value: transfers.filter(t => ['approved','completed'].includes((t.status||'').toLowerCase())).length, color: 'green' },
          { label: 'Pending',   value: transfers.filter(t => !['approved','completed'].includes((t.status||'').toLowerCase())).length, color: 'orange' },
          { label: 'Total Amount', value: `$${totalAmount.toLocaleString()}`, color: 'purple' },
        ].map(s => (
          <div key={s.label} className={`acc-stat-card ${s.color}`}>
            <div className="acc-stat-label">{s.label}</div>
            <div className="acc-stat-value">{s.value}</div>
          </div>
        ))}
      </div>

      {/* ── Table ── */}
      <div className="table-content-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem' }}>
          <h3 className="acc-card-title" style={{ margin: 0 }}>Fund Transfers</h3>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            {/* Search */}
            <div className="tabs-search-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'var(--acc-surface)', padding: '0.2rem 0.5rem', borderRadius: 'var(--acc-radius)', border: '1px solid var(--acc-border)', boxShadow: 'var(--acc-shadow-sm)' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--acc-text-muted)', marginLeft: '0.5rem' }}>
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="tabs-search-input"
                placeholder="Search transfers..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ border: 'none', outline: 'none', padding: '0.4rem', fontSize: '0.9rem', background: 'transparent', minWidth: '180px', color: 'var(--acc-text-main)' }}
              />
            </div>

            {/* Export */}
            <div ref={exportRef} style={{ position: 'relative' }}>
              <button
                className="acc-btn-secondary"
                onClick={() => setShowExport(p => !p)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.8 }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export ▾
              </button>
              {showExport && (
                <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 0.5rem)', background: 'var(--acc-surface)', border: '1px solid var(--acc-border)', borderRadius: 'var(--acc-radius)', boxShadow: 'var(--acc-shadow-lg)', zIndex: 50, minWidth: '160px', overflow: 'hidden' }}>
                  {['CSV', 'Excel', 'PDF', 'Print'].map(f => (
                    <div key={f} onClick={() => handleExport(f)}
                      style={{ padding: '0.75rem 1rem', cursor: 'pointer', fontSize: '0.9rem', color: 'var(--acc-text-main)', borderBottom: f !== 'Print' ? '1px solid var(--acc-border)' : 'none', transition: 'background 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--acc-bg)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      Export {f}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add Transfer */}
            <button className="acc-btn-primary" onClick={openModal}>
              <span className="plus-icon" style={{ fontSize: '1.2rem', lineHeight: 1 }}>+</span> Add Transfer
            </button>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="products-table" style={{ minWidth: '680px' }}>
            <thead>
              <tr>
                <th>Transfer #</th>
                <th>Date</th>
                <th>From Account</th>
                <th>To Account</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th>Note</th>
                <th style={{ textAlign: 'center' }}>Status</th>
                <th style={{ textAlign: 'center' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.id}>
                  <td><span style={{ fontWeight: 700, color: 'var(--etpos-primary,#2563eb)' }}>{t.id}</span></td>
                  <td style={{ fontSize: '0.85rem' }}>{t.transfer_date || t.date || '—'}</td>
                  <td>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{ color: '#ef4444', fontWeight: 700 }}>↑</span>{accountName(t.from_account_id || t.from_account)}
                    </span>
                  </td>
                  <td>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{ color: '#10b981', fontWeight: 700 }}>↓</span>{accountName(t.to_account_id || t.to_account)}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>
                    ${(parseFloat(t.amount) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td style={{ color: '#64748b', fontSize: '0.85rem' }}>{t.additional_notes || t.note || '—'}</td>
                  <td style={{ textAlign: 'center' }}><StatusBadge status={t.status} /></td>
                  <td style={{ textAlign: 'center' }}>
                    <button onClick={() => handleDelete(t.id)}
                      style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: '4px', padding: '0.2rem 0.5rem', cursor: 'pointer', fontSize: '0.75rem', color: '#ef4444' }}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔄</div>
                    <div style={{ fontWeight: 600 }}>No transfers found</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Add Transfer Modal ── */}
      {showModal && (
        <div
          onClick={() => setShowModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: '8px',
              width: '95%',
              maxWidth: '540px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 25px 50px rgba(0,0,0,0.2)'
            }}
          >
            {/* Header */}
            <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#1e293b' }}>Add Transfer</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>✕</button>
            </div>

            {/* Body — scrollable */}
            <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>

              {/* Reference No */}
              <div style={{ marginBottom: '1.1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>
                  Reference No:
                  <span title="Auto-generated if left blank" style={{ background: '#06b6d4', color: '#fff', borderRadius: '50%', width: '16px', height: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, cursor: 'help', flexShrink: 0 }}>ℹ</span>
                </label>
                <input value={form.ref} onChange={e => setForm(p => ({ ...p, ref: e.target.value }))} style={inputStyle} />
              </div>

              {/* Transfer From */}
              <div style={{ marginBottom: '1.1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>
                  Transfer from:<span style={{ color: '#ef4444' }}>*</span>
                </label>
                <select
                  value={form.from_account_id}
                  onChange={e => setForm(p => ({ ...p, from_account_id: e.target.value }))}
                  style={{ ...inputStyle, border: errors.from ? '1px solid #ef4444' : '1px solid #cbd5e1' }}
                >
                  <option value="">Please Select</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {errors.from && <div style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: '0.25rem' }}>Please select a source account</div>}
              </div>

              {/* Transfer To */}
              <div style={{ marginBottom: '1.1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>
                  Transfer To:<span style={{ color: '#ef4444' }}>*</span>
                </label>
                <select
                  value={form.to_account_id}
                  onChange={e => setForm(p => ({ ...p, to_account_id: e.target.value }))}
                  style={{ ...inputStyle, border: errors.to ? '1px solid #ef4444' : '1px solid #cbd5e1' }}
                >
                  <option value="">Please Select</option>
                  {accounts.filter(a => String(a.id) !== String(form.from_account_id)).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {errors.to && <div style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: '0.25rem' }}>Please select a destination account</div>}
              </div>

              {/* Amount */}
              <div style={{ marginBottom: '1.1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>
                  Amount:<span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="number" min="0"
                  value={form.amount}
                  onChange={e => setForm(p => ({ ...p, amount: e.target.value }))}
                  style={{ ...inputStyle, border: errors.amount ? '1px solid #ef4444' : '1px solid #cbd5e1' }}
                />
                {errors.amount && <div style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: '0.25rem' }}>Please enter a valid amount</div>}
              </div>

              {/* Date */}
              <div style={{ marginBottom: '1.1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>
                  Date:<span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="date"
                  value={form.date}
                  onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
                  style={{ ...inputStyle, border: errors.date ? '1px solid #ef4444' : '1px solid #cbd5e1' }}
                />
                {errors.date && <div style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: '0.25rem' }}>Please select a date</div>}
              </div>

              {/* Note */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>Note</label>
                <textarea
                  value={form.note}
                  onChange={e => setForm(p => ({ ...p, note: e.target.value }))}
                  placeholder="Note"
                  rows={4}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: '90px', fontFamily: 'inherit' }}
                />
              </div>
            </div>

            {/* Footer — centered */}
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'center', gap: '0.75rem' }}>
              <button
                onClick={handleSubmit}
                disabled={saving}
                style={{ background: saving ? '#a5b4fc' : '#5b21b6', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.55rem 2rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}
                onMouseEnter={e => { if (!saving) e.target.style.background = '#4c1d95'; }}
                onMouseLeave={e => { if (!saving) e.target.style.background = saving ? '#a5b4fc' : '#5b21b6'; }}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: '#334155', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.55rem 2rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}
                onMouseEnter={e => e.target.style.background = '#1e293b'}
                onMouseLeave={e => e.target.style.background = '#334155'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transfer;
